import { cn } from "@bcc/lib/utils";
import { generateTestId } from "@client-shared";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { SelectionPopoverPanel } from "./selection-popover-panel";
import { RADIX_10, useSelectionPopover } from "./use-selection-popover";
import type { SelectionMode } from "./use-selection-popover";
export type { SelectionMode } from "./use-selection-popover";

interface SelectionPopoverTriggerProps {
  testIdComponent: string;
  selectedCount: number;
  allLeafCount: number;
  currentPageCount: number;
  maxLimit: number;
  showMaxLabel: boolean;
  onApplySelection: (mode: SelectionMode, quantity?: number) => void;
  onClear: () => void;
  className?: string;
}

export const SelectionPopoverTrigger = ({
  testIdComponent,
  selectedCount,
  allLeafCount,
  currentPageCount,
  maxLimit,
  showMaxLabel,
  onApplySelection,
  onClear,
  className,
}: SelectionPopoverTriggerProps) => {
  const { t } = useTranslation();

  const setTestId = (description: string) => generateTestId(testIdComponent, description);

  const {
    open,
    setOpen,
    selectionMode,
    setSelectionMode,
    specificQuantity,
    setSpecificQuantity,
    triggerRef,
    panelRef,
    isOverMax,
    isApplyDisabled,
  } = useSelectionPopover(maxLimit);

  const handleToggleOpen = React.useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  const handleSelectAll = React.useCallback(() => {
    onApplySelection("all");
  }, [onApplySelection]);

  const handleSelectCurrentPage = React.useCallback(() => {
    onApplySelection("currentPage");
  }, [onApplySelection]);

  const handleClear = React.useCallback(() => {
    onClear();
    setOpen(false);
  }, [onClear, setOpen]);

  const handleSpecificSubmit = React.useCallback(() => {
    const qty = Number.parseInt(specificQuantity, RADIX_10);
    if (!Number.isNaN(qty) && qty > 0) {
      onApplySelection("specific", qty);
    }
  }, [onApplySelection, specificQuantity]);

  if (selectedCount === 0) return null;

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        ref={triggerRef}
        onClick={handleToggleOpen}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid={setTestId("trigger")}
        className={cn(
          "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-sm font-medium",
          "transition-colors hover:bg-accent/60",
          className
        )}
      >
        {t("dataTable.selectionPopover.selectedCount", {
          count: selectedCount,
        })}
        <ChevronDown className={cn("size-3.5 transition-transform duration-150", open && "rotate-180")} />
      </button>

      {open && (
        <SelectionPopoverPanel
          panelRef={panelRef}
          testIdComponent={testIdComponent}
          selectionMode={selectionMode}
          setSelectionMode={setSelectionMode}
          onSelectAll={handleSelectAll}
          onSelectCurrentPage={handleSelectCurrentPage}
          onClear={handleClear}
          onSpecificSubmit={handleSpecificSubmit}
          specificQuantity={specificQuantity}
          setSpecificQuantity={setSpecificQuantity}
          isApplyDisabled={isApplyDisabled}
          isOverMax={isOverMax}
          allCount={allLeafCount}
          currentPageCount={currentPageCount}
          maxLimit={maxLimit}
          showMaxLabel={showMaxLabel}
        />
      )}
    </span>
  );
};
